export interface JDesignVueResolverOptions {
  /**
   * import style css or sass with components
   * @default 'css'
   */
  importStyle?: boolean | 'css' | 'sass'
  /**
   * use commonjs lib & source css or scss for ssr
   * @default false
   */
  ssr?: boolean
  /**
   * exclude component name, if match do not resolve the name
   */
  exclude?: RegExp
  /**
   * component names that have no styles
   */
  noStylesComponents?: string[]
}

type SideEffectsInfo = string | string[] | undefined

interface ComponentInfo {
  name?: string
  from: string
  sideEffects?: SideEffectsInfo
}

interface ComponentResolverObject {
  type: 'component'
  resolve: (name: string) => ComponentInfo | undefined
}

const packageName = '@jd/jdesign-vue'

/** Component partial name → directory under es/components */
const directoryOverrides: Record<string, string> = {
  VisuallyHidden: 'visual-hidden',
  RovingFocusItem: 'roving-focus-group',
  PopperArrow: 'popper',
  PopperContent: 'popper',
  PopperTrigger: 'popper',
  AutoResizer: 'table-v2',
  OptionImgLabel: 'select',
  OnlyChild: 'slot',
  CheckboxCard: 'checkbox',
  GuideHint: 'guide',
  ResultDialog: 'result',
  UploadFallbackIcon: 'upload',
  LoadingDirective: 'loading',
  LoadingService: 'loading',
  PopoverDirective: 'popover',
  StatisticCardList: 'statistic-card',
  StatisticCardGrid: 'statistic-card',
  StatisticCardFormula: 'statistic-card',
  StatisticCardGroup: 'statistic-card',
  StatisticCardSuperGroup: 'statistic-card',
}

const defaultNoStylesComponents = [
  'JdInfiniteScroll',
  'JdFocusTrap',
  'JdRovingFocusGroup',
  'JdRovingFocusItem',
  'JdCollapseTransition',
  'JdTeleport',
  'JdVisuallyHidden',
  'JdOnlyChild',
  'JdOptionImgLabel',
  'JdAutoResizer',
  'JdUploadFallbackIcon',
]

function kebabCase(key: string) {
  const result = key.replace(/([A-Z])/g, ' $1').trim()
  return result.split(/\s+/).join('-').toLowerCase()
}

function getSideEffects(
  dirName: string,
  options: Required<Pick<JDesignVueResolverOptions, 'importStyle' | 'ssr'>> &
    JDesignVueResolverOptions
): SideEffectsInfo {
  const { importStyle, ssr } = options
  if (!importStyle) return undefined

  // SSR imports theme files directly, bypassing component style dependencies.
  if (ssr && (dirName === 'statistic-card' || dirName === 'input-tag')) {
    const styles =
      dirName === 'input-tag'
        ? ['base', 'tag', 'button', 'popper', 'popover', 'input', 'input-tag']
        : [
            'base',
            'button',
            'checkbox',
            'tooltip',
            'popper',
            'scrollbar',
            'statistic-card',
          ]
    return styles.map((style) =>
      importStyle === 'sass'
        ? `${packageName}/theme-chalk/src/${style}.scss`
        : `${packageName}/theme-chalk/${
            style === 'base' ? style : `jd-${style}`
          }.css`
    )
  }

  if (importStyle === 'sass') {
    return ssr
      ? [
          `${packageName}/theme-chalk/src/base.scss`,
          `${packageName}/theme-chalk/src/${dirName}.scss`,
        ]
      : [
          `${packageName}/es/components/base/style/index`,
          `${packageName}/es/components/${dirName}/style/index`,
        ]
  }

  if (importStyle === true || importStyle === 'css') {
    return ssr
      ? [
          `${packageName}/theme-chalk/base.css`,
          `${packageName}/theme-chalk/jd-${dirName}.css`,
        ]
      : [
          `${packageName}/es/components/base/style/css`,
          `${packageName}/es/components/${dirName}/style/css`,
        ]
  }

  return undefined
}

function resolveComponent(
  name: string,
  options: Required<Pick<JDesignVueResolverOptions, 'importStyle' | 'ssr'>> &
    JDesignVueResolverOptions
): ComponentInfo | undefined {
  if (options.exclude && name.match(options.exclude)) return

  if (!name.match(/^Jd[A-Z]/)) return
  // 避免与 JdPro* 系列冲突,交给 JDesignVueProResolver 处理
  if (name.startsWith('JdPro')) return

  const partialName = name.slice(2) // remove `Jd`
  const dirName = directoryOverrides[partialName] || kebabCase(partialName)

  const noStyles = [
    ...defaultNoStylesComponents,
    ...(options.noStylesComponents || []),
  ]

  return {
    name,
    from: `${packageName}/${options.ssr ? 'lib' : 'es'}`,
    sideEffects: noStyles.includes(name)
      ? undefined
      : getSideEffects(dirName, options),
  }
}

/**
 * Resolver for `@jd/jdesign-vue`
 *
 * @example
 * ```ts
 * import Components from 'unplugin-vue-components/vite'
 * import { JDesignVueResolver } from '@jd/jdesign-vue/resolver'
 *
 * Components({
 *   resolvers: [JDesignVueResolver()],
 * })
 * ```
 */
export function JDesignVueResolver(
  options: JDesignVueResolverOptions = {}
): ComponentResolverObject {
  const resolvedOptions = {
    ssr: false,
    importStyle: 'css' as const,
    ...options,
  }

  return {
    type: 'component',
    resolve: (name: string) => resolveComponent(name, resolvedOptions),
  }
}
